import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, useSnackbar } from '@c2fo/liquidity';
import { BuyerCurrencyGroupWithStats } from '@/enterprise/components/InvoiceCentral/useGroupByBuyerCurrency';
import { downloadPlayableInvoicesCsv } from '@/enterprise/data/downloadPlayableInvoicesCsv';
import useAdjustmentInvoiceStats from '@/enterprise/data/useAdjustmentInvoiceStats';
import useLockedInvoiceStats from '@/enterprise/data/useLockedInvoiceStats';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import useFeature from '@/lib/features';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { buildInvoiceCentralCsvFilename } from '@/utils/buildInvoiceCsvFilename';
import { LinkOption } from './InvoiceCentralLayout';
import useDownloadInvoicesUrl from './useDownloadInvoicesUrl';

interface DownloadInvoiceProps {
  makerOrganizationUuid: string;
  currentBuyer: BuyerCurrencyGroupWithStats;
  currency: string;
  currentTable: LinkOption;
}

const DownloadInvoicesComponent = ({
  currentBuyer,
  makerOrganizationUuid,
  currency,
  currentTable,
}: DownloadInvoiceProps) => {
  const { trackEnterpriseEvent } = useReporting();
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [invoiceApiMigrationEnabled] = useFeature('enterprise-ui_invoiceApiMigration');

  const { data: eligibleInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: currentBuyer.takerMarkets,
  });
  const { data: ineligibleInvoiceStats } = useLockedInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: currentBuyer.takerMarkets,
  });
  const { data: adjustmentStats } = useAdjustmentInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: currentBuyer.takerMarkets,
  });

  const disableDownload =
    (currentTable === 'eligible' && eligibleInvoiceStats?.totalCount === 0) ||
    (currentTable === 'ineligible' && ineligibleInvoiceStats?.totalCount === 0) ||
    (currentTable === 'adjustments' && adjustmentStats?.totalCount === 0);

  const downloadInvoicesUrl = useDownloadInvoicesUrl({
    makerOrganizationUuid,
    takerMarketIds: currentBuyer.takerMarkets.map((tm) => ({
      takerId: tm.takerDivisionId,
      marketId: tm.legacyMarketId,
    })),
    currency,
    type: currentTable ?? 'eligible',
  });

  // v1 IC SEA: same as legacy Node — all invoices for current buyer taker-markets (no table filters).
  // Filter-matched download deferred until download moves into tab controls (see decisions doc).
  const useSeaEligibleDownload = invoiceApiMigrationEnabled && currentTable === 'eligible';

  const trackDownloadSubmitted = () => {
    // SUPP-19158: same analytics event as legacy ButtonLink path — verify name/payload in PR review.
    trackEnterpriseEvent('invoice-central::download-invoices::submitted', {
      invoiceType: currentTable,
      // Array of ids used for the download endpoint
      takerMarketIds: currentBuyer.takerMarkets.map((tm) => ({
        takerId: tm.takerDivisionId,
        marketId: tm.legacyMarketId,
      })),
    });
  };

  const handleSeaEligibleDownload = async () => {
    if (disableDownload || isDownloading) {
      return;
    }

    trackDownloadSubmitted();
    setIsDownloading(true);
    try {
      await downloadPlayableInvoicesCsv({
        makerOrganizationUuid,
        takerMarkets: currentBuyer.takerMarkets,
        filename: buildInvoiceCentralCsvFilename({
          makerName: currentBuyer.makerOrganizationName,
          currency,
          invoiceType: currentTable,
        }),
      });
    } catch {
      showSnackbar({ message: t('core.downloadError') });
    } finally {
      setIsDownloading(false);
    }
  };

  if (useSeaEligibleDownload) {
    return (
      <Button
        className="w-full md:w-fit"
        disabled={disableDownload || isDownloading}
        variant="tertiary"
        onClick={() => {
          void handleSeaEligibleDownload();
        }}
      >
        {t('core.downloadInvoices')}
      </Button>
    );
  }

  return (
    <ButtonLink
      className="w-full md:w-fit"
      onClick={trackDownloadSubmitted}
      disabled={disableDownload}
      variant="tertiary"
      to={downloadInvoicesUrl}
    >
      {t('core.downloadInvoices')}
    </ButtonLink>
  );
};

const DownloadInvoices = (props: DownloadInvoiceProps) => {
  const { t } = useTranslation();

  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <Button className="w-full md:w-fit" disabled variant="tertiary">
          {t('core.downloadInvoices')}
        </Button>
      )}
      ErrorComponent={() => (
        <Button className="w-full md:w-fit" disabled variant="tertiary">
          {t('core.downloadInvoices')}
        </Button>
      )}
    >
      <DownloadInvoicesComponent {...props} />
    </QueryBoundaries>
  );
};

export default DownloadInvoices;
